/**
 * VerifyEmail — landing for the verification link. Reads ?token from the URL,
 * POSTs it to /auth/verify-email, shows result.
 *
 * Methodology: empty states are statements, not apologies. If the link is
 * stale, we tell the user plainly and offer the resend path.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2, Mail } from 'lucide-react';
import api from '../lib/api';

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState('verifying'); // verifying | success | error
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setState('error');
      setMsg('Verification link is missing its token. Request a new email.');
      return;
    }
    (async () => {
      try {
        const r = await api.post('/auth/verify-email', { token });
        if (r.data?.verified) {
          setState('success');
          setMsg(r.data.email || '');
        } else {
          setState('error');
          setMsg('Verification failed.');
        }
      } catch (e) {
        setState('error');
        setMsg(e.response?.data?.detail || 'Verification failed.');
      }
    })();
  }, [params]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0A0A0A' }} data-testid="verify-email-page">
      <div className="max-w-md w-full border border-[#D4A84A]/20 rounded-md p-7 md:p-8" style={{ background: '#0F0F11' }}>
        <div className="flex items-center gap-2 mb-4">
          <Mail size={16} className="text-[#D4A84A]" />
          <span className="font-hud text-[10px] md:text-xs text-[#D4A84A] tracking-widest uppercase">TSOGN — Email Verification</span>
        </div>
        {state === 'verifying' && (
          <div className="flex items-center gap-3 font-mono text-sm text-white/85" data-testid="verify-email-progress">
            <Loader2 size={16} className="animate-spin text-[#2F8A92]" />
            Confirming your email…
          </div>
        )}
        {state === 'success' && (
          <div data-testid="verify-email-success">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 size={18} className="text-[#2F8A92]" />
              <span className="font-historical text-2xl text-[#D4A84A] font-light">Email confirmed</span>
            </div>
            <p className="font-mono text-sm text-white/80 leading-relaxed">
              {msg ? <strong className="text-white">{msg}</strong> : 'Your email'} is verified. Your account is fully active.
            </p>
            <button
              onClick={() => navigate('/')}
              data-testid="verify-email-return-btn"
              className="mt-5 font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-4 py-2 hover:bg-[#D4A84A]/85"
            >
              RETURN TO TSOGN
            </button>
          </div>
        )}
        {state === 'error' && (
          <div data-testid="verify-email-error">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle size={18} className="text-[#FF6B6B]" />
              <span className="font-historical text-xl text-[#FF6B6B] font-light">Link not valid</span>
            </div>
            <p className="font-mono text-sm text-white/85 leading-relaxed">
              {msg}
            </p>
            <p className="font-mono text-xs text-white/55 mt-3 leading-relaxed">
              Verification links expire after 24 hours. Sign in and request a new email from the verification banner.
            </p>
            <button
              onClick={() => navigate('/')}
              className="mt-5 font-hud text-xs tracking-widest text-white/85 border border-[#2F8A92]/30 px-4 py-2 hover:text-[#2F8A92]"
            >
              SIGN IN
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
